import type { CustomerGroup, Machine } from '../services/BackendDataService';
import { translateCountryName } from './sapMetadataTranslations';

// Types for globe data
export interface CountryData {
  country: string;
  translatedCountry: string;
  machineCount: number;
  machines: Machine[];
  factories: any[];
  lat: number;
  lng: number;
  color?: string;
}

// Comprehensive country coordinates mapping
const COUNTRY_COORDINATES: Record<string, { lat: number; lng: number }> = {
  // German SAP names (original data)
  'Deutschland': { lat: 51.1657, lng: 10.4515 },
  'Österreich': { lat: 47.5162, lng: 14.5501 },
  'Schweiz': { lat: 46.8182, lng: 8.2275 },
  'Frankreich': { lat: 46.2276, lng: 2.2137 },
  'Italien': { lat: 41.8719, lng: 12.5674 },
  'Spanien': { lat: 40.4637, lng: -3.7492 },
  'Niederlande': { lat: 52.1326, lng: 5.2913 },
  'Belgien': { lat: 50.5039, lng: 4.4699 },
  'Polen': { lat: 51.9194, lng: 19.1451 },
  'Tschechien': { lat: 49.8175, lng: 15.4730 },
  'Ungarn': { lat: 47.1625, lng: 19.5033 },
  'Slowakei': { lat: 48.6690, lng: 19.6990 },
  'Slowenien': { lat: 46.1512, lng: 14.9955 },
  'Kroatien': { lat: 45.1000, lng: 15.2000 },
  'Rumänien': { lat: 45.9432, lng: 24.9668 },
  'Bulgarien': { lat: 42.7339, lng: 25.4858 },
  'Griechenland': { lat: 39.0742, lng: 21.8243 },
  'Türkei': { lat: 38.9637, lng: 35.2433 },
  'Russland': { lat: 61.5240, lng: 105.3188 },
  'Vereinigtes Königreich': { lat: 55.3781, lng: -3.4360 },
  'Irland': { lat: 53.4129, lng: -8.2439 },
  'Norwegen': { lat: 60.4720, lng: 8.4689 },
  'Schweden': { lat: 60.1282, lng: 18.6435 },
  'Dänemark': { lat: 56.2639, lng: 9.5018 },
  'Finnland': { lat: 61.9241, lng: 25.7482 },
  'Estland': { lat: 58.5953, lng: 25.0136 },
  'Lettland': { lat: 56.8796, lng: 24.6032 },
  'Litauen': { lat: 55.1694, lng: 23.8813 },
  
  // English translations (fallback)
  'Germany': { lat: 51.1657, lng: 10.4515 },
  'Austria': { lat: 47.5162, lng: 14.5501 },
  'Switzerland': { lat: 46.8182, lng: 8.2275 },
  'France': { lat: 46.2276, lng: 2.2137 },
  'Italy': { lat: 41.8719, lng: 12.5674 },
  'Spain': { lat: 40.4637, lng: -3.7492 },
  'Netherlands': { lat: 52.1326, lng: 5.2913 },
  'Belgium': { lat: 50.5039, lng: 4.4699 },
  'Poland': { lat: 51.9194, lng: 19.1451 },
  'Czech Republic': { lat: 49.8175, lng: 15.4730 },
  'Hungary': { lat: 47.1625, lng: 19.5033 },
  'Slovakia': { lat: 48.6690, lng: 19.6990 },
  'Slovenia': { lat: 46.1512, lng: 14.9955 },
  'Croatia': { lat: 45.1000, lng: 15.2000 },
  'Romania': { lat: 45.9432, lng: 24.9668 },
  'Bulgaria': { lat: 42.7339, lng: 25.4858 },
  'Greece': { lat: 39.0742, lng: 21.8243 },
  'Turkey': { lat: 38.9637, lng: 35.2433 },
  'Russia': { lat: 61.5240, lng: 105.3188 },
  'Ukraine': { lat: 48.3794, lng: 31.1656 },
  'United Kingdom': { lat: 55.3781, lng: -3.4360 },
  'UK': { lat: 55.3781, lng: -3.4360 },
  'Ireland': { lat: 53.4129, lng: -8.2439 },
  'Portugal': { lat: 39.3999, lng: -8.2245 },
  'Norway': { lat: 60.4720, lng: 8.4689 },
  'Sweden': { lat: 60.1282, lng: 18.6435 },
  'Denmark': { lat: 56.2639, lng: 9.5018 },
  'Finland': { lat: 61.9241, lng: 25.7482 },
  'Estonia': { lat: 58.5953, lng: 25.0136 },
  'Latvia': { lat: 56.8796, lng: 24.6032 },
  'Lithuania': { lat: 55.1694, lng: 23.8813 },
  
  // Asia-Pacific
  'Japan': { lat: 36.2048, lng: 138.2529 },
  'China': { lat: 35.8617, lng: 104.1954 },
  'India': { lat: 20.5937, lng: 78.9629 },
  'Australia': { lat: -25.2744, lng: 133.7751 },
  'South Korea': { lat: 35.9078, lng: 127.7669 },
  'Korea': { lat: 35.9078, lng: 127.7669 },
  'Südkorea': { lat: 35.9078, lng: 127.7669 },
  'Singapore': { lat: 1.3521, lng: 103.8198 },
  'Malaysia': { lat: 4.2105, lng: 101.9758 },
  'Thailand': { lat: 15.8700, lng: 100.9925 },
  'Indonesia': { lat: -0.7893, lng: 113.9213 },
  'Philippines': { lat: 12.8797, lng: 121.7740 },
  'Vietnam': { lat: 14.0583, lng: 108.2772 },
  'Taiwan': { lat: 23.6978, lng: 120.9605 },
  'Hong Kong': { lat: 22.3193, lng: 114.1694 },
  'New Zealand': { lat: -40.9006, lng: 174.8860 },
  'Bangladesh': { lat: 23.6850, lng: 90.3563 },
  'Pakistan': { lat: 30.3753, lng: 69.3451 },
  'Sri Lanka': { lat: 7.8731, lng: 80.7718 },
  'Myanmar': { lat: 21.9162, lng: 95.9560 },
  'Cambodia': { lat: 12.5657, lng: 104.9910 },
  'Laos': { lat: 19.8563, lng: 102.4955 },
  
  // German names for Asian countries
  'Indien': { lat: 20.5937, lng: 78.9629 },
  'Bangladesch': { lat: 23.6850, lng: 90.3563 },
  'Singapur': { lat: 1.3521, lng: 103.8198 },
  'Indonesien': { lat: -0.7893, lng: 113.9213 },
  'Philippinen': { lat: 12.8797, lng: 121.7740 },
  'Australien': { lat: -25.2744, lng: 133.7751 },
  'Neuseeland': { lat: -40.9006, lng: 174.8860 },
  
  // Americas
  'United States': { lat: 39.8283, lng: -98.5795 },
  'USA': { lat: 39.8283, lng: -98.5795 },
  'Canada': { lat: 56.1304, lng: -106.3468 },
  'Mexico': { lat: 23.6345, lng: -102.5528 },
  'Brazil': { lat: -14.2350, lng: -51.9253 },
  'Argentina': { lat: -38.4161, lng: -63.6167 },
  'Chile': { lat: -35.6751, lng: -71.5430 },
  'Colombia': { lat: 4.5709, lng: -74.2973 },
  'Peru': { lat: -9.1900, lng: -75.0152 },
  'Venezuela': { lat: 6.4238, lng: -66.5897 },
  
  // Middle East & Africa
  'Saudi Arabia': { lat: 23.8859, lng: 45.0792 },
  'UAE': { lat: 23.4241, lng: 53.8478 },
  'United Arab Emirates': { lat: 23.4241, lng: 53.8478 },
  'Israel': { lat: 31.0461, lng: 34.8516 },
  'South Africa': { lat: -30.5595, lng: 22.9375 },
  'Egypt': { lat: 26.8206, lng: 30.8025 },
  'Nigeria': { lat: 9.0820, lng: 8.6753 },
  'Kenya': { lat: -0.0236, lng: 37.9062 },
  'Morocco': { lat: 31.7917, lng: -7.0926 },
  'Tunisia': { lat: 33.8869, lng: 9.5375 },
  'Algeria': { lat: 28.0339, lng: 1.6596 },
  'Libya': { lat: 26.3351, lng: 17.2283 },
  'Ghana': { lat: 7.9465, lng: -1.0232 },
  'Ethiopia': { lat: 9.1450, lng: 40.4897 },
  'Tanzania': { lat: -6.3690, lng: 34.8888 },
  'Uganda': { lat: 1.3733, lng: 32.2903 },
  'Rwanda': { lat: -1.9403, lng: 29.8739 },
  'Iran': { lat: 32.4279, lng: 53.6880 },
  'Iraq': { lat: 33.2232, lng: 43.6793 },
  'Jordan': { lat: 30.5852, lng: 36.2384 },
  'Lebanon': { lat: 33.8547, lng: 35.8623 },
  'Qatar': { lat: 25.3548, lng: 51.1839 },
  'Kuwait': { lat: 29.3117, lng: 47.4818 },
  'Bahrain': { lat: 26.0667, lng: 50.5577 },
  'Oman': { lat: 21.4735, lng: 55.9754 }
};

/**
 * Get coordinates for a country with translation support
 */
export function getCountryCoordinates(countryName: string): { lat: number; lng: number } | null {
  // Try original name first
  if (COUNTRY_COORDINATES[countryName]) {
    return COUNTRY_COORDINATES[countryName];
  }
  
  // Try translated name
  const translated = translateCountryName(countryName);
  if (translated && translated !== countryName && COUNTRY_COORDINATES[translated]) {
    return COUNTRY_COORDINATES[translated];
  }
  
  // Try some common variations
  const variations = [
    countryName.toLowerCase(),
    countryName.toUpperCase(),
    countryName.charAt(0).toUpperCase() + countryName.slice(1).toLowerCase()
  ];
  
  for (const variation of variations) {
    const match = Object.keys(COUNTRY_COORDINATES).find(
      key => key.toLowerCase() === variation.toLowerCase()
    );
    if (match) {
      return COUNTRY_COORDINATES[match];
    }
  }
  
  // Default coordinates (center of Europe) for unknown countries
  console.warn(`Unknown country coordinates for: ${countryName}`);
  return { lat: 50.0, lng: 10.0 };
}

/**
 * Get machines for a specific country (function expected by GlobeView)
 */
export function getMachinesByCountry(countryName: string, sapData: Record<string, CustomerGroup>): Machine[] {
  const machines: Machine[] = [];
  
  Object.values(sapData).forEach(customerGroup => {
    customerGroup.regionen?.forEach((region: any) => {
      region.laender?.forEach((country: any) => {
        if (country.land === countryName) {
          country.kunden?.forEach((customer: any) => {
            customer.werke?.forEach((factory: any) => {
              factory.linien?.forEach((line: any) => {
                if (line.maschinen && Array.isArray(line.maschinen)) {
                  line.maschinen.forEach((machine: any) => {
                    // Add factory and line context to machine
                    const enrichedMachine: Machine = {
                      ...machine,
                      factoryName: factory.werkName,
                      lineId: line.linieBK,
                      lineName: line.linieName,
                      country: countryName,
                      region: region.region,
                      customerName: customer.kundenName
                    };
                    machines.push(enrichedMachine);
                  });
                }
              });
            });
          });
        }
      });
    });
  });
  
  return machines;
}

/**
 * Aggregate machine data by country for globe visualization
 */
export function aggregateDataByCountry(sapData: Record<string, CustomerGroup>): CountryData[] {
  const countryDataMap: Record<string, CountryData> = {};
  const missingCountries = new Set<string>();
  
  Object.values(sapData).forEach(customerGroup => {
    customerGroup.regionen?.forEach((region: any) => {
      region.laender?.forEach((country: any) => {
        const countryName = country.land;
        const coordinates = getCountryCoordinates(countryName);
        
        if (coordinates) {
          if (!countryDataMap[countryName]) {
            countryDataMap[countryName] = {
              country: countryName,
              translatedCountry: translateCountryName(countryName) || countryName,
              machineCount: 0,
              machines: [],
              factories: [],
              lat: coordinates.lat,
              lng: coordinates.lng,
              color: '#3b82f6'
            };
          }
          
          country.kunden?.forEach((customer: any) => {
            customer.werke?.forEach((factory: any) => {
              // Add factory to the list if not already added
              if (!countryDataMap[countryName].factories.find(f => f.werkName === factory.werkName)) {
                countryDataMap[countryName].factories.push(factory);
              }
              
              factory.linien?.forEach((line: any) => {
                if (line.maschinen && Array.isArray(line.maschinen)) {
                  line.maschinen.forEach((machine: any) => {
                    // Add factory and line context to machine
                    const enrichedMachine: Machine = {
                      ...machine,
                      factoryName: factory.werkName,
                      lineId: line.linieBK,
                      lineName: line.linieName,
                      country: countryName,
                      region: region.region,
                      customerName: customer.kundenName
                    };
                    countryDataMap[countryName].machines.push(enrichedMachine);
                    countryDataMap[countryName].machineCount++;
                  });
                }
              });
            });
          });
        } else {
          missingCountries.add(countryName);
        }
      });
    });
  });
  
  // Log missing countries for debugging
  if (missingCountries.size > 0 && import.meta.env.DEV) {
    console.warn('🌍 Countries missing coordinates:', Array.from(missingCountries));
  }
  
  // Convert to array and add colors based on machine count
  const countryDataArray = Object.values(countryDataMap).map(data => ({
    ...data,
    color: getMachineCountColor(data.machineCount)
  }));
  
  if (import.meta.env.DEV) {
    console.log('🌍 Countries with coordinates:', countryDataArray.map(c => c.country));
  }
  
  return countryDataArray.sort((a, b) => b.machineCount - a.machineCount);
}

/**
 * Get color based on machine count for visualization
 */
function getMachineCountColor(count: number): string {
  if (count >= 50) return '#dc2626'; // red-600
  if (count >= 20) return '#ea580c'; // orange-600
  if (count >= 10) return '#d97706'; // amber-600
  if (count >= 5) return '#ca8a04';  // yellow-600
  if (count >= 1) return '#65a30d';  // lime-600
  return '#6b7280'; // gray-500
}

/**
 * Generate globe points for react-globe.gl
 */
export function generateGlobePoints(countryData: CountryData[]) {
  return countryData.map(data => ({
    lat: data.lat,
    lng: data.lng,
    size: Math.max(0.1, Math.min(2, data.machineCount / 10)), // Scale size based on machine count
    color: data.color || '#3b82f6',
    country: data.country,
    translatedCountry: data.translatedCountry,
    machineCount: data.machineCount,
    machines: data.machines
  }));
}

/**
 * Filter country data to show only countries with data availability
 */
export function filterCountriesWithData(countryData: CountryData[], showOnlyWithData: boolean): CountryData[] {
  if (!showOnlyWithData) {
    // Show only countries with machines that have data
    return countryData.filter(data => 
      data.machines.some(machine => machine.datenVerfuegbar)
    );
  }
  
  // Show all countries
  return countryData;
}